'use client';

/**
 * 用户列表页
 * 
 * @页面路径 /organization/members
 * @权限要求 user:read:*
 * @文档参考 docs/modules/organization/05-ui-interaction-spec.md - 页面1: 用户列表页
 * 
 * 核心功能：
 * - 用户列表展示（支持分页、排序）
 * - 关键词搜索（用户名/邮箱/员工编号）
 * - 多维度筛选（部门/状态/角色）
 * - 用户状态管理（激活/停用/暂停/离职）
 * - 批量操作（批量选择）
 * - 快捷操作（查看/编辑/更多）
 */

import { useEffect, useState } from 'react';
import { useRouter } from 'next/navigation';
import { useTranslation } from '@/hooks/useTranslation';
import { usePermissions } from '@/hooks/usePermissions';
import { useDebounce } from '@/hooks/useDebounce';
import { toast } from '@/lib/toast';
import { useConfirm } from '@/components/common/feedback/ConfirmProvider';
import { PageState } from '@/components/common/feedback/PageState';
import { handleApiError } from '@/lib/error-handler';
import {
  getUsers,
  triggerSync,
  getDepartments,
  getRoles,
  updateUser,
  type User,
  type UserStatus,
  type Department,
  type Role,
} from '@/services/api/organization';
import { 
  Search, 
  Plus, 
  RefreshCw, 
  MoreHorizontal, 
  Eye, 
  Edit,
  Upload,
  FileDown,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { colors, typography } from '@/styles/theme';
import BatchImportUserMembershipsDialog from '@/features/organization/components/BatchImportUserMembershipsDialog';
import BatchImportWorkCityDialog from '@/features/organization/components/BatchImportWorkCityDialog';
import UserAvatar from '@/features/organization/components/UserAvatar';
import { MapPin } from 'lucide-react';

/**
 * 分页组件
 * 符合飞书设计风格的分页控制器
 */
const Pagination = ({ 
  currentPage, 
  totalPages, 
  total,
  onPageChange,
  translations
}: { 
  currentPage: number; 
  totalPages: number; 
  total: number;
  onPageChange: (page: number) => void;
  translations: any;
}) => {
  const t = translations;
  return (
    <div className="flex items-center justify-between px-6 py-3" style={{ borderTop: `1px solid ${colors.border}` }}>
      <div className="text-sm" style={{ color: colors.textSecondary }}>
        {t.common.page} {currentPage} {t.common.pageOf} {totalPages} {t.common.pages} • {t.common.total} {total} {t.common.items}
      </div>
      <div className="flex gap-2">
        <Button
          variant="outline"
          size="sm"
          onClick={() => onPageChange(currentPage - 1)}
          disabled={currentPage === 1}
        >
          {t.organization.membersList.prevPage}
        </Button>
        <Button
          variant="outline"
          size="sm"
          onClick={() => onPageChange(currentPage + 1)}
          disabled={currentPage === totalPages}
        >
          {t.organization.membersList.nextPage}
        </Button>
      </div>
    </div>
  );
};

export default function UsersPage() {
  const router = useRouter();
  const { t } = useTranslation();
  const confirm = useConfirm();
  const { hasPermission, isAdmin } = usePermissions();

  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [hasLoadedOnce, setHasLoadedOnce] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [departments, setDepartments] = useState<Department[]>([]);
  const [roles, setRoles] = useState<Role[]>([]);

  // 筛选状态
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState<UserStatus | ''>('');
  const [departmentFilter, setDepartmentFilter] = useState('');
  const [roleFilter, setRoleFilter] = useState('');

  // 防抖搜索词（300ms 延迟）
  const debouncedSearchQuery = useDebounce(searchQuery, 300);

  // 分页状态
  const [page, setPage] = useState(1);
  const [pageSize] = useState(20);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(1);

  // 批量选择
  const [selectedUsers, setSelectedUsers] = useState<Set<string>>(new Set());

  // 批量导入对话框
  const [showBatchImportDialog, setShowBatchImportDialog] = useState(false);
  const [showWorkCityDialog, setShowWorkCityDialog] = useState(false);

  // 权限
  const canCreate = isAdmin || hasPermission('user:create');
  const canUpdate = isAdmin || hasPermission('user:update');
  const canDelete = isAdmin || hasPermission('user:delete');
  const canSync = isAdmin || hasPermission('organization:sync');
  const canAssignRoles = isAdmin || hasPermission('role:assign');

  useEffect(() => {
    loadUsers();
  }, [debouncedSearchQuery, statusFilter, departmentFilter, roleFilter, page]);

  useEffect(() => {
    loadDepartments();
    loadRoles();
  }, []);

  const loadUsers = async () => {
    try {
      setLoading(true);
      const data = await getUsers({
        keyword: debouncedSearchQuery || undefined,
        status: statusFilter || undefined,
        departmentId: departmentFilter || undefined,
        page,
        pageSize,
      });

      setUsers(data.items || []);
      setTotal(data.total || 0);
      setTotalPages(data.totalPages || 1);
    } catch (error: any) {
      handleApiError(error, t.locale, { prefix: t.common.loadFailed });
      setUsers([]);
    } finally {
      setLoading(false);
      setHasLoadedOnce(true);
    }
  };

  const loadDepartments = async () => {
    try {
      const data = await getDepartments();
      // getDepartments 返回 Department[] 或 { items: Department[] } 格式
      if (Array.isArray(data)) {
        setDepartments(data);
      } else if (data && typeof data === 'object' && 'items' in data) {
        setDepartments((data as any).items || []);
      } else {
        setDepartments([]);
      }
    } catch (error) {
      console.error('Failed to load departments:', error);
    }
  };

  const loadRoles = async () => {
    try {
      const data = await getRoles();
      setRoles(data || []);
    } catch (error) {
      console.error('Failed to load roles:', error);
    }
  };

  const handleLdapSync = async () => {
    const confirmed = await confirm({
      title: t.organization.sync.confirmSync,
      variant: 'primary',
    });
    if (!confirmed) return;

    try {
      setSyncing(true);
      await triggerSync();
      toast.success(t.organization.sync.syncSuccess);
      await loadUsers();
    } catch (error: any) {
      handleApiError(error, t.locale, { prefix: t.organization.sync.syncFailed });
    } finally {
      setSyncing(false);
    }
  };

  const handleStatusChange = async (userId: string, newStatus: UserStatus) => {
    try {
      await updateUser(userId, { status: newStatus });
      toast.success(t.common.updateSuccess);
      await loadUsers();
    } catch (error: any) {
      handleApiError(error, t.locale, { prefix: t.common.updateFailed });
    }
  };

  /**
   * 用户状态徽章
   * 按照文档规范：docs/modules/organization/05-ui-interaction-spec.md - 状态徽章样式
   */
  const getStatusBadge = (status: UserStatus) => {
    const config = {
      ACTIVE: { bg: '#e6f7ed', text: '#00b42a', icon: '🟢', label: t.organization.statusActive },
      INACTIVE: { bg: '#f7f8fa', text: '#8f959e', icon: '🟡', label: t.organization.statusInactive },
      SUSPENDED: { bg: '#fff7e6', text: '#ff7d00', icon: '🟠', label: t.organization.statusSuspended },
      TERMINATED: { bg: '#ffece8', text: '#f53f3f', icon: '🔴', label: t.organization.statusTerminated },
    };

    const style = config[status] || config.ACTIVE;
    return (
      <Badge 
        variant="secondary"
        className="inline-flex items-center gap-1 px-2 py-1 text-xs rounded"
        style={{ backgroundColor: style.bg, color: style.text }}
        data-testid={`status-badge-${status}`}
      >
        <span>{style.icon}</span>
        <span>{style.label}</span>
      </Badge>
    );
  };

  const toggleSelectUser = (userId: string) => {
    const newSelection = new Set(selectedUsers);
    if (newSelection.has(userId)) {
      newSelection.delete(userId);
    } else {
      newSelection.add(userId);
    }
    setSelectedUsers(newSelection);
  };

  const toggleSelectAll = () => {
    if (selectedUsers.size === users.length) {
      setSelectedUsers(new Set());
    } else {
      setSelectedUsers(new Set(users.map(u => u.id)));
    }
  };

  // 首次加载骨架屏
  if (loading && users.length === 0 && !hasLoadedOnce) {
    return (
      <div className="h-full flex flex-col" style={{ backgroundColor: colors.bgSecondary }}>
        {/* 标题栏骨架 */}
        <div className="bg-white border-b" style={{ borderColor: colors.border }}>
          <div className="px-6 h-12 flex items-center">
            <Skeleton className="h-6 w-48" />
          </div>
        </div>
        {/* 主内容骨架 */}
        <div className="flex-1 overflow-auto p-6">
          <Skeleton className="h-96 w-full" data-testid="loading-skeleton" />
        </div>
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col" style={{ backgroundColor: colors.bgSecondary }}>
      {/* 页面标题栏 - 符合统一高度规范 h-12 (48px) */}
      <div className="bg-white border-b" style={{ borderColor: colors.border }}>
        <div className="px-6 h-12 flex items-center justify-between">
          <h1 className={typography.h4} data-testid="page-title">
            {t.organization.membersList.title}
          </h1>
          <div className="flex items-center gap-2">
            {canCreate && (
              <Button
                variant="outline"
                size="sm"
                onClick={() => router.push('/organization/members/import')}
                className="flex items-center gap-2"
                data-action="import"
              >
                <Upload className="w-4 h-4" />
                {t.organization.membersList.batchImport}
              </Button>
            )}
            {canSync && (
              <Button
                variant="outline"
                size="sm"
                onClick={handleLdapSync}
                disabled={syncing}
                className="flex items-center gap-2"
              >
                <RefreshCw className={`w-4 h-4 ${syncing ? 'animate-spin' : ''}`} />
                {syncing ? t.organization.membersList.syncing : t.organization.membersList.syncFromLDAP}
              </Button>
            )}
            {canUpdate && (
              <Button
                variant="outline"
                size="sm"
                onClick={() => setShowBatchImportDialog(true)}
                className="flex items-center gap-2"
              >
                <FileDown className="w-4 h-4" />
                {t.organization.membersList.batchImportMemberships || '批量导入成员关系'}
              </Button>
            )}
            {canUpdate && (
              <Button
                variant="outline"
                size="sm"
                onClick={() => setShowWorkCityDialog(true)}
                className="flex items-center gap-2"
                data-action="import-work-city"
              >
                <MapPin className="w-4 h-4" />
                {t.organization.membersList.batchImportWorkCity}
              </Button>
            )}
            {canCreate && (
              <Button
                size="sm"
                onClick={() => router.push('/organization/members/new')}
                className="flex items-center gap-2"
                style={{ backgroundColor: colors.primary }}
                data-action="create-user"
              >
                <Plus className="w-4 h-4" />
                {t.organization.membersList.createUser}
              </Button>
            )}
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white border-b px-6 py-3" style={{ borderColor: colors.border }}>
        <div className="flex items-center gap-3">
          {/* Search */}
          <div className="flex-1 max-w-md relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4" style={{ color: colors.textTertiary }} data-testid="search-icon" />
            <Input
              value={searchQuery}
              onChange={(e) => {
                setSearchQuery(e.target.value);
                setPage(1); // 搜索时重置页码
              }}
              placeholder={t.organization.members.searchPlaceholder}
              className="w-full h-8 pl-9 pr-3 text-sm"
              data-testid="user-search-input"
            />
            {searchQuery && searchQuery !== debouncedSearchQuery && (
              <div className="absolute right-3 top-1/2 -translate-y-1/2" data-testid="search-loading">
                <div className="w-4 h-4 border-2 border-blue-600 border-t-transparent rounded-full animate-spin" />
              </div>
            )}
            {searchQuery && (
              <button
                onClick={() => {
                  setSearchQuery('');
                  setPage(1);
                }}
                className="absolute right-3 top-1/2 -translate-y-1/2 hover:opacity-70"
                data-action="clear-search"
                aria-label={t.common.clear}
              >
                <span className="text-xs">✕</span>
              </button>
            )}
          </div>

          {/* Department Filter */}
          <Select value={departmentFilter || 'all'} onValueChange={(value) => setDepartmentFilter(value === 'all' ? '' : value)}>
            <SelectTrigger className="w-[180px] h-8" name="department">
              <SelectValue placeholder={t.organization.department} />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">{t.organization.membersList.allDepartments}</SelectItem>
              {departments.map((dept) => (
                <SelectItem key={dept.id} value={dept.id}>
                  {dept.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          {/* Status Filter */}
          <Select value={statusFilter || 'all'} onValueChange={(value) => setStatusFilter(value === 'all' ? '' : (value as UserStatus))}>
            <SelectTrigger className="w-[150px] h-8" name="status" data-testid="status-filter">
              <SelectValue placeholder={t.organization.status} />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">{t.organization.membersList.allStatus}</SelectItem>
              <SelectItem value="ACTIVE">{t.organization.active}</SelectItem>
              <SelectItem value="INACTIVE">{t.organization.inactive}</SelectItem>
              <SelectItem value="SUSPENDED">{t.organization.suspended}</SelectItem>
              <SelectItem value="TERMINATED">{t.organization.terminated}</SelectItem>
            </SelectContent>
          </Select>

          {/* Role Filter */}
          <Select value={roleFilter || 'all'} onValueChange={(value) => setRoleFilter(value === 'all' ? '' : value)}>
            <SelectTrigger className="w-[150px] h-8" name="role">
              <SelectValue placeholder={t.organization.membersList.roleLabel} />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">{t.organization.membersList.allRoles}</SelectItem>
              {roles.map((role) => (
                <SelectItem key={role.id} value={role.id}>
                  {role.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Table */}
      <div className="flex-1 overflow-auto">
        <div className="bg-white m-6 rounded-lg shadow-sm overflow-hidden">
          <Table data-testid="user-table">
            <TableHeader className="bg-[#fafafa] [&_tr]:border-b-0">
              <TableRow className="hover:bg-transparent border-0">
                <TableHead className="w-[40px] text-[#666]">
                  <Checkbox
                    checked={selectedUsers.size === users.length && users.length > 0}
                    onCheckedChange={toggleSelectAll}
                  />
                </TableHead>
                <TableHead className="w-[140px] text-[#666]" data-column="displayName">{t.organization.membersList.displayName}</TableHead>
                <TableHead className="w-[120px] text-[#666]" data-column="username">{t.organization.membersList.username}</TableHead>
                <TableHead className="w-[200px] text-[#666]" data-column="email">{t.organization.membersList.email}</TableHead>
                <TableHead className="w-[150px] text-[#666]" data-column="department">{t.organization.membersList.department}</TableHead>
                <TableHead className="w-[80px] text-[#666]" data-column="status">{t.organization.membersList.statusColumn}</TableHead>
                <TableHead className="w-[120px] text-[#666] text-right" data-column="actions">{t.organization.membersList.actions}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody className="divide-y">
              {users.length === 0 && !loading ? (
                <TableRow>
                  <TableCell colSpan={7} className="h-64 text-center">
                    {debouncedSearchQuery || statusFilter || departmentFilter || roleFilter ? (
                      <div data-testid="empty-search">
                        <PageState
                          variant="empty"
                          title={t.organization.membersList.noMatchingUsers}
                          description={t.organization.membersList.tryOtherFilters}
                        />
                      </div>
                    ) : (
                      <div data-testid="empty-state">
                        <PageState
                          variant="empty"
                          title={t.organization.membersList.noUsersYet}
                          description={t.organization.membersList.clickToCreate}
                          actionLabel={canCreate ? t.organization.membersList.createFirstUser : undefined}
                          onAction={canCreate ? () => router.push('/organization/members/new') : undefined}
                        />
                      </div>
                    )}
                  </TableCell>
                </TableRow>
              ) : users.length > 0 ? (
                users.map((user) => (
                  <TableRow 
                    key={user.id} 
                    data-testid="user-row"
                    className="cursor-pointer hover:bg-gray-50 border-0"
                    onClick={() => router.push(`/organization/members/${user.id}`)}
                  >
                    <TableCell onClick={(e) => e.stopPropagation()}>
                      <Checkbox
                        checked={selectedUsers.has(user.id)}
                        onCheckedChange={() => toggleSelectUser(user.id)}
                      />
                    </TableCell>
                    <TableCell className="text-sm" data-column="displayName">
                      <div className="flex items-center gap-2 min-w-0">
                        <UserAvatar
                          avatar={user.avatar}
                          displayName={user.displayName || user.username}
                          size={28}
                        />
                        <span className="truncate text-[#1f2329]">
                          {user.displayName || user.username}
                        </span>
                      </div>
                    </TableCell>
                    <TableCell className="text-sm" data-column="username">{user.username}</TableCell>
                    <TableCell className="text-sm text-[#646a73]" data-column="email">{user.email}</TableCell>
                    <TableCell className="text-sm text-[#1f2329]" data-column="department">
                      {user.department?.name || <span className="text-[#8f959e]">-</span>}
                    </TableCell>
                    <TableCell data-column="status">{getStatusBadge(user.status)}</TableCell>
                    <TableCell data-column="actions" className="text-right" onClick={(e) => e.stopPropagation()}>
                      <div className="flex items-center justify-end gap-2">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => router.push(`/organization/members/${user.id}`)}
                          data-action="view"
                        >
                          <Eye className="w-4 h-4" />
                        </Button>
                        {canUpdate && (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => router.push(`/organization/members/${user.id}/edit`)}
                            data-action="edit"
                          >
                            <Edit className="w-4 h-4" />
                          </Button>
                        )}
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="sm" data-action="more">
                              <MoreHorizontal className="w-4 h-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end" data-testid="user-actions-dropdown">
                            {canAssignRoles && user.status === 'ACTIVE' && (
                              <DropdownMenuItem data-action="assign-roles">
                                {t.organization.membersList.assignRoles}
                              </DropdownMenuItem>
                            )}
                            {canUpdate && user.status === 'ACTIVE' && (
                              <DropdownMenuItem 
                                data-action="suspend"
                                onClick={() => handleStatusChange(user.id, 'SUSPENDED')}
                              >
                                {t.organization.membersList.suspend}
                              </DropdownMenuItem>
                            )}
                            {canUpdate && user.status === 'SUSPENDED' && (
                              <DropdownMenuItem 
                                data-action="unsuspend"
                                onClick={() => handleStatusChange(user.id, 'ACTIVE')}
                              >
                                {t.organization.membersList.resume}
                              </DropdownMenuItem>
                            )}
                            {canUpdate && user.status === 'ACTIVE' && (
                              <DropdownMenuItem 
                                data-action="deactivate"
                                onClick={() => handleStatusChange(user.id, 'INACTIVE')}
                              >
                                {t.organization.membersList.deactivate}
                              </DropdownMenuItem>
                            )}
                            {canUpdate && user.status === 'INACTIVE' && (
                              <DropdownMenuItem 
                                data-action="activate"
                                onClick={() => handleStatusChange(user.id, 'ACTIVE')}
                              >
                                {t.organization.membersList.activate}
                              </DropdownMenuItem>
                            )}
                            {canUpdate && user.status !== 'TERMINATED' && (
                              <DropdownMenuItem 
                                data-action="terminate"
                                onClick={() => handleStatusChange(user.id, 'TERMINATED')}
                                className="text-red-600"
                              >
                                {t.organization.membersList.terminate}
                              </DropdownMenuItem>
                            )}
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </div>
                    </TableCell>
                  </TableRow>
                ))
              ) : null}
            </TableBody>
          </Table>

          {/* 分页器 - 数据超过一页时显示 */}
          {total > pageSize && (
            <div data-testid="pagination">
              <Pagination
                currentPage={page}
                totalPages={totalPages}
                total={total}
                onPageChange={setPage}
                translations={t}
              />
            </div>
          )}
        </div>
      </div>

      {/* 批量导入成员关系对话框 */}
      {showBatchImportDialog && (
        <BatchImportUserMembershipsDialog
          isOpen={showBatchImportDialog}
          onClose={() => setShowBatchImportDialog(false)}
          onSuccess={loadUsers}
          // ✅ 不传 currentOrganizationId，让对话框显示组织选择器
        />
      )}

      <BatchImportWorkCityDialog
        isOpen={showWorkCityDialog}
        onClose={() => setShowWorkCityDialog(false)}
        onSuccess={loadUsers}
      />
    </div>
  );
}
